/**
 * Print-Only Dashboard Page for PDF Generation
 * Renders comprehensive dashboard snapshot with all widgets
 *
 * URL: /print/dashboard/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ClientProfileCard from '../../components/dashboard/ClientProfileCard';
import BusinessProfileCard from '../../components/dashboard/BusinessProfileCard';
import AdvisoryTeamCard from '../../components/dashboard/AdvisoryTeamCard';
import BusinessValuationCard from '../../components/dashboard/BusinessValuationCard';
import GapAssessmentsCard from '../../components/dashboard/GapAssessmentsCard';
import TaskManagerCard from '../../components/dashboard/TaskManagerCard';
import WealthGapCard from '../../components/dashboard/WealthGapCard';
import ProfitGapCard from '../../components/dashboard/ProfitGapCard';
import ValueGapCard from '../../components/dashboard/ValueGapCard';
import { useDateFormatter } from '../../utils/dateUtils';
import '../../print.css';

const PrintDashboard = () => {
  const { businessId } = useParams();
  const { formatDate } = useDateFormatter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    businessProfile: null,
    latestValuation: null,
    assessment: null,
    personalAssessment: null,
    taskStats: null,
    wealthGap: null,
    profitGap: null,
    valueGap: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch all required data in parallel with better error handling
        const fetchWithLog = async (url, name) => {
          try {
            const response = await fetch(url, { headers });
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`[PrintDashboard] ${name} error:`, errorText);
              return null;
            }
            return response;
          } catch (error) {
            console.error(`[PrintDashboard] ${name} fetch failed:`, error);
            return null;
          }
        };

        const [
          profileResponse,
          valuationResponse,
          assessmentResponse,
          personalAssessmentResponse,
          taskStatsResponse,
          wealthGapResponse,
          profitGapResponse,
          valueGapResponse
        ] = await Promise.all([
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, 'profile'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/valuation/history`, 'valuation'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/assessment/current`, 'assessment'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/current`, 'personal assessment'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/tasks/stats`, 'task stats'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/wealth-gap`, 'wealth gap'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/profit-gap`, 'profit gap'),
          fetchWithLog(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/value-gap`, 'value gap')
        ]);

        const profileData = profileResponse ? await profileResponse.json() : null;
        const valuationData = valuationResponse ? await valuationResponse.json() : null;
        const assessmentData = assessmentResponse ? await assessmentResponse.json() : null;
        const personalAssessmentData = personalAssessmentResponse ? await personalAssessmentResponse.json() : null;
        const taskStatsData = taskStatsResponse ? await taskStatsResponse.json() : null;
        const wealthGapData = wealthGapResponse ? await wealthGapResponse.json() : null;
        const profitGapData = profitGapResponse ? await profitGapResponse.json() : null;
        const valueGapData = valueGapResponse ? await valueGapResponse.json() : null;

        // Extract latest valuation from history
        let latestValuation = null;
        if (valuationData?.valuations && valuationData.valuations.length > 0) {
          latestValuation = valuationData.valuations[0];
          // Map fields if needed
          if (latestValuation.low_range && latestValuation.high_range) {
            latestValuation.valuation_low = latestValuation.low_range;
            latestValuation.valuation_high = latestValuation.high_range;
          }
          // Extract revenue and EBITDA from input_data
          if (latestValuation.input_data) {
            try {
              const inputData = typeof latestValuation.input_data === 'string'
                ? JSON.parse(latestValuation.input_data)
                : latestValuation.input_data;
              latestValuation.revenue = latestValuation.revenue || inputData.revenue;
              latestValuation.ebitda = latestValuation.ebitda || inputData.ebitda;
            } catch (e) {
              console.error('[PrintDashboard] Error parsing input_data:', e);
            }
          }
          // Fallback to business profile if no revenue/EBITDA in valuation
          if (profileData?.data?.profile) {
            latestValuation.revenue = latestValuation.revenue || profileData.data.profile.revenue;
            latestValuation.ebitda = latestValuation.ebitda || profileData.data.profile.ebitda;
          }
        }

        setData({
          businessProfile: profileData?.data?.profile || null,
          latestValuation: latestValuation,
          assessment: assessmentData || null,  // Direct object, no wrapper
          personalAssessment: personalAssessmentData || null,  // Direct object, no wrapper
          taskStats: taskStatsData?.stats || null,
          wealthGap: wealthGapData?.wealth_gap || null,
          profitGap: profitGapData?.profit_gap || null,
          valueGap: valueGapData?.value_gap || null
        });

        setLoading(false);
      } catch (err) {
        console.error('[PrintDashboard] Error loading dashboard data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && !error) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      const businessName = data?.businessProfile?.business_name || 'Business';
      document.title = `${businessName} - Dashboard Report`;
    }
  }, [loading, error, data]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const businessName = data?.businessProfile?.business_name || 'Business Name Not Set';
  const industry = data?.businessProfile?.industry;

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Dashboard Report</h1>
          <div className="report-type">Comprehensive Exit Planning Overview</div>
        </div>

        <div className="cover-business-info">
          <h2>{businessName}</h2>
          {industry && <p className="industry">{industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Exit Planning Dashboard Report</p>
        </div>
      </div>

      {/* Page 2 - Client & Business Profile */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        {/* Client & Business Profile Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', marginBottom: '0.05in' }}>
          {/* Client Profile Section */}
          <div>
            <h1 className="section-title">Client Profile</h1>
            <div className="profile-card-print">
              <ClientProfileCard
                businessProfile={data.businessProfile}
                isExpanded={true}
                onToggle={() => {}} // No-op for print
              />
            </div>
          </div>

          {/* Business Profile Section */}
          <div>
            <h1 className="section-title">Business Profile</h1>
            <div className="profile-card-print">
              <BusinessProfileCard
                businessProfile={data.businessProfile}
                isExpanded={true}
                onToggle={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Page 3 - Advisory Team */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        {/* Advisory Team Section */}
        <div>
          <h1 className="section-title">Advisory Team</h1>
          <div className="profile-card-print">
            <AdvisoryTeamCard
              businessProfile={data.businessProfile}
              isExpanded={true}
              onToggle={() => { }}
            />
          </div>
        </div>
      </div>

      {/* Page 4 - Gap Assessments, Business Valuation, Value Gap & Profit Gap */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        {/* Row 1: Gap Assessments & Business Valuation */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.2in',
          marginBottom: '0.15in'
        }}>
          <div className="dashboard-card-print">
            <GapAssessmentsCard
              assessment={data.assessment}
              personalAssessment={data.personalAssessment}
            />
          </div>

          <div className="dashboard-card-print">
            <BusinessValuationCard
              latestValuation={data.latestValuation}
              formatDate={formatDate}
            />
          </div>
        </div>

        {/* Row 2: Value Gap & Profit Gap */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.2in',
          marginBottom: '0.1in'
        }}>
          <div className="dashboard-card-print">
            <ValueGapCard
              valueGap={data.valueGap}
              setShowValueGapDetails={() => { }}
              handleRecalculateValueGap={() => { }}
            />
          </div>

          <div className="dashboard-card-print">
            <ProfitGapCard
              profitGap={data.profitGap}
              setShowProfitGapDetails={() => { }}
              handleRecalculateProfitGap={() => { }}
            />
          </div>
        </div>
      </div>

      {/* Page 5 - Wealth Gap & Task Manager */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        {/* Row 1: Wealth Gap (wider single column) */}
        <div style={{
          marginBottom: '0.15in'
        }}>
          <div className="dashboard-card-print" style={{
            width: '90%',
            margin: '0 auto'
          }}>
            <WealthGapCard
              wealthGap={data.wealthGap}
              setShowWealthGapDetails={() => { }}
              setShowWealthGapCalculator={() => { }}
            />
          </div>
        </div>

        {/* Row 2: Task Manager */}
        <div style={{
          marginBottom: '0.1in'
        }}>
          <div className="dashboard-card-print" style={{
            width: '90%',
            margin: '0 auto'
          }}>
            <TaskManagerCard taskStats={data.taskStats} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintDashboard;
